// CB:BEGIN contractor-availability-toggle
import React, { useState, useEffect } from 'react';
import { useAuthFetch } from '../../lib/useAuthFetch';

type AvailabilityStatus = 'AVAILABLE' | 'BUSY' | 'UNAVAILABLE';

const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  AVAILABLE: 'Available for new projects',
  BUSY: 'Busy — limited availability',
  UNAVAILABLE: 'Not available',
};

const STATUS_COLORS: Record<AvailabilityStatus, string> = {
  AVAILABLE: '#16a34a',
  BUSY: '#d97706',
  UNAVAILABLE: '#dc2626',
};

interface Props {
  compact?: boolean;
}

const AvailabilityToggle: React.FC<Props> = ({ compact = false }) => {
  const authFetch = useAuthFetch();
  const [status, setStatus] = useState<AvailabilityStatus>('AVAILABLE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    authFetch('/api/contractor/availability')
      .then(r => r.json())
      .then(data => {
        if (data.availabilityStatus) setStatus(data.availabilityStatus);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as AvailabilityStatus;
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/contractor/availability', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availabilityStatus: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to update availability');
      } else {
        setStatus(next);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: STATUS_COLORS[status],
            flexShrink: 0,
          }}
        />
        <select
          value={status}
          onChange={handleChange}
          disabled={saving}
          style={{
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '2px 6px',
            fontSize: 13,
            color: '#374151',
            background: '#fff',
            cursor: 'pointer',
          }}
          aria-label="Set your availability status"
        >
          {(Object.keys(STATUS_LABELS) as AvailabilityStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        {saving && <span style={{ fontSize: 12, color: '#6b7280' }}>Saving...</span>}
        {error && <span style={{ fontSize: 12, color: '#dc2626' }}>{error}</span>}
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '12px 16px',
        background: '#f9fafb',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
            Availability Status
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Let homeowners and the CoreBids team know your current capacity.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: STATUS_COLORS[status],
              flexShrink: 0,
            }}
          />
          <select
            value={status}
            onChange={handleChange}
            disabled={saving}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 14,
              color: '#374151',
              background: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
            aria-label="Set your availability status"
          >
            {(Object.keys(STATUS_LABELS) as AvailabilityStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          {saving && <span style={{ fontSize: 13, color: '#6b7280' }}>Saving...</span>}
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
};

export default AvailabilityToggle;
// CB:END contractor-availability-toggle
